export const TEAM_ROLE_IDS = Object.freeze({
  OWNER: 1,
  USER: 2,
  TAX_AGENT_OWNER: 3,
  TAX_AGENT_USER: 4,
  ADMIN: 5,
  DEVELOPER: 6,
  FINANCE: 10,
  PREPARER: 11,
});

const ROLE_META = [
  { id: TEAM_ROLE_IDS.OWNER, key: "owner" },
  { id: TEAM_ROLE_IDS.ADMIN, key: "admin" },
  { id: TEAM_ROLE_IDS.FINANCE, key: "finance" },
  { id: TEAM_ROLE_IDS.DEVELOPER, key: "developer" },
  { id: TEAM_ROLE_IDS.PREPARER, key: "preparer" },
  { id: TEAM_ROLE_IDS.USER, key: "user" },
];

const FALLBACK_TITLES = {
  owner: "Owner",
  admin: "Admin",
  finance: "Finance",
  developer: "Developer",
  preparer: "Preparer",
  user: "User",
};

const FALLBACK_DESCRIPTIONS = {
  owner: "Full access to company settings, users, and applications",
  admin: "Full access to company settings, users, and applications",
  finance: "Access to billing, payments, invoices, and financial information",
  developer: "Access to integrations, APIs, and technical settings",
  preparer: "Can prepare and manage data for filings and submissions",
  user: "Standard access to assigned applications and features",
};

function toRoleId(value) {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export function resolveTeamRoleId(...sources) {
  for (const source of sources) {
    if (source == null) continue;
    if (typeof source !== "object") {
      const id = toRoleId(source);
      if (id != null) return id;
      continue;
    }
    const id = toRoleId(
      source.role ??
        source.role_id ??
        source.roleId ??
        source.user?.role ??
        source.user?.role_id
    );
    if (id != null) return id;
  }
  return null;
}

export function getMemberAppCountry(...sources) {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    if (source.app_country && typeof source.app_country === "object") {
      return source.app_country;
    }
    if (source.user?.app_country && typeof source.user.app_country === "object") {
      return source.user.app_country;
    }
  }
  return {};
}

export function getTeamRoleItems(t) {
  return ROLE_META.map(({ id, key }) => ({
    id,
    key,
    title: t
      ? t(`team.roleItems.${key}.title`, { defaultValue: FALLBACK_TITLES[key] })
      : FALLBACK_TITLES[key],
    description: t
      ? t(`team.roleItems.${key}.description`, {
          defaultValue: FALLBACK_DESCRIPTIONS[key],
        })
      : FALLBACK_DESCRIPTIONS[key],
  }));
}

const EXTRA_TITLES = {
  [TEAM_ROLE_IDS.TAX_AGENT_OWNER]: { key: "taxAgentOwner", title: "Tax agent owner" },
  [TEAM_ROLE_IDS.TAX_AGENT_USER]: { key: "taxAgentUser", title: "Tax agent user" },
};

export function getTeamRoleTitle(roleId, t) {
  const id = toRoleId(roleId);
  if (id == null) return t ? t("team.unknownRole", { defaultValue: "Unknown" }) : "Unknown";
  const item = getTeamRoleItems(t).find((role) => role.id === id);
  if (item) return item.title;
  const extra = EXTRA_TITLES[id];
  if (extra) {
    return t
      ? t(`sharedUi.teamRoles.${extra.key}`, { ns: "translation", defaultValue: extra.title })
      : extra.title;
  }
  return t
    ? t("sharedUi.teamRoles.roleN", { ns: "translation", id, defaultValue: `Role ${id}` })
    : `Role ${id}`;
}
